import React, { useState, useEffect, useContext } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Text,
    VStack,
    Icon,
    Box,
    useDisclosure,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertCircle } from 'react-icons/fi';
import alertService from '../services/alertService';
import AuthContext from '../services/AuthContext';

const PersistentAlert = () => {
    const [activeAlerts, setActiveAlerts] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const { user } = useContext(AuthContext);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        if (user) {
            fetchMyAlerts();
        }
    }, [user]);

    const fetchMyAlerts = async () => {
        try {
            const response = await alertService.getMyAlerts();
            if (response.data && response.data.length > 0) {
                setActiveAlerts(response.data);
                onOpen();
            }
        } catch (error) {
            console.error('Error fetching alerts:', error);
        }
    };

    const handleNext = async () => {
        try {
            // Acknowledge the current alert
            await alertService.acknowledgeAlert(currentAlert._id);

            if (currentIndex < activeAlerts.length - 1) {
                setCurrentIndex(prev => prev + 1);
            } else {
                onClose();
            }
        } catch (error) {
            console.error('Error acknowledging alert:', error);
            // Still proceed even if acknowledgment fails
            if (currentIndex < activeAlerts.length - 1) {
                setCurrentIndex(prev => prev + 1);
            } else {
                onClose();
            }
        }
    };

    if (activeAlerts.length === 0) return null;

    const currentAlert = activeAlerts[currentIndex];

    return (
        <AnimatePresence>
            {isOpen && (
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    size="full"
                    motionPreset="scale"
                >
                    <ModalOverlay backdropFilter="blur(15px)" bg="blackAlpha.800" />
                    <ModalContent
                        as={motion.div}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        dir="rtl"
                        bg="white"
                        m={0}
                        borderRadius={0}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Box position="fixed" top={0} left={0} h="15px" bg="red.600" w="100%" />

                        <VStack spacing={8} maxW="800px" w="100%" px={6} textAlign="center">
                            <motion.div
                                animate={{
                                    scale: [1, 1.1, 1],
                                    rotate: [0, 5, -5, 0]
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                <Icon as={FiAlertCircle} boxSize={{ base: 24, md: 40 }} color="red.600" />
                            </motion.div>

                            <VStack spacing={4}>
                                <Text
                                    fontFamily="Cairo, sans-serif"
                                    fontSize={{ base: "3xl", md: "5xl" }}
                                    color="red.600"
                                    fontWeight="900"
                                >
                                    تنبيه إداري هام جداً
                                </Text>
                                <Box h="2px" bg="red.200" w="100px" />
                            </VStack>

                            <Text
                                fontFamily="Cairo, sans-serif"
                                fontSize={{ base: "xl", md: "3xl" }}
                                color="gray.800"
                                lineHeight="base"
                                fontWeight="600"
                                bg="red.50"
                                p={8}
                                borderRadius="2xl"
                                borderWidth="2px"
                                borderColor="red.100"
                                boxShadow="inner"
                            >
                                {currentAlert?.message}
                            </Text>

                            <Button
                                bg="red.600"
                                color="white"
                                _hover={{ bg: 'red.700', transform: 'scale(1.05)', boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                                _active={{ bg: 'red.800', transform: 'scale(0.95)' }}
                                size="lg"
                                height="70px"
                                px={16}
                                borderRadius="full"
                                onClick={handleNext}
                                fontFamily="Cairo, sans-serif"
                                fontWeight="bold"
                                fontSize="2xl"
                                transition="all 0.2s"
                                boxShadow="0 10px 15px -3px rgba(220, 38, 38, 0.3)"
                            >
                                {currentIndex < activeAlerts.length - 1 ? 'التنبيه التالي' : 'قرأت التنبيه وأوافق'}
                            </Button>
                        </VStack>

                        <Box position="fixed" bottom={4} opacity={0.3}>
                            <Text fontFamily="Cairo" fontSize="sm">منصة كوبرا التعليمية - نظام الإشعارات العاجلة</Text>
                        </Box>
                    </ModalContent>
                </Modal>
            )}
        </AnimatePresence>
    );
};

export default PersistentAlert;
